import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getDetalleProductoAll } from "../../../../services/financiero/productosEntregasService";

// 🔥 Helpers
const formatNumber = (v) =>
  Number(v || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 });

const formatCurrency = (v) =>
  `$${Number(v || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`;

export default function ExportExcelButton({ data }) {
  const handleExport = async () => {
    try {
      if (!data || data.length === 0) {
        message.warning("No hay datos para exportar");
        return;
      }

      // 🔥 TRAER TODO EL DETALLE UNA SOLA VEZ
      const allDetalles = await getDetalleProductoAll();

      const workbook = new ExcelJS.Workbook();

      // 🟦 HOJA PRINCIPAL
      const sheet = workbook.addWorksheet("Productos OC");

      // 🟦 HOJA DETALLE
      const detailSheet = workbook.addWorksheet("Detalle");

      // ===============================
      // 🔹 HOJA PRINCIPAL
      // ===============================
      sheet.columns = [
        { header: "PO", key: "Numero_PO", width: 14 },
        { header: "Proyecto", key: "nombre_proyecto", width: 35 },
        { header: "Municipio", key: "municipio", width: 20 },
        { header: "Cantidad", key: "cantidad_total", width: 15 },
        { header: "Valor Total", key: "valor_total", width: 20 },
        { header: "Entregado", key: "cantidad_entregada", width: 15 },
        { header: "Pendiente", key: "pendiente", width: 15 },
        { header: "% Entrega", key: "porcentaje_entrega", width: 15 },
        { header: "Estado", key: "estado", width: 15 }
      ];

      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center" };

      headerRow.eachCell(cell => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" }
        };
        cell.font = { color: { argb: "FFFFFF" }, bold: true };
      });

      data.forEach(p => {
        const porcentaje = Number(p.porcentaje_entrega || 0);

        let estado = "Parcial";
        if (porcentaje === 100) estado = "Entregado";
        else if (porcentaje === 0) estado = "Sin entregas";

        sheet.addRow({
          Numero_PO: p.Numero_PO,
          nombre_proyecto: p.nombre_proyecto,
          municipio: p.municipio,
          cantidad_total: formatNumber(p.cantidad_total),
          valor_total: formatCurrency(p.valor_total),
          cantidad_entregada: formatNumber(p.cantidad_entregada),
          pendiente: formatNumber(p.pendiente),
          porcentaje_entrega: `${porcentaje}%`,
          estado
        });
      });

      // ===============================
      // 🔹 HOJA DETALLE
      // ===============================
      detailSheet.columns = [
        { header: "PO", key: "Numero_PO", width: 14 },
        { header: "Proyecto", key: "nombre_proyecto", width: 35 },
        { header: "Municipio", key: "municipio", width: 20 },
        { header: "Producto", key: "nombre_producto", width: 40 },
        { header: "Cantidad", key: "cantidad_total", width: 15 },
        { header: "Entregado", key: "cantidad_entregada", width: 15 },
        { header: "Pendiente", key: "pendiente", width: 15 },
        { header: "Valor Unitario", key: "valor_unitario_COP", width: 18 },
        { header: "Valor Total", key: "valor_total", width: 18 },
        { header: "% Entrega", key: "porcentaje_entrega", width: 15 }
      ];

      const detailHeader = detailSheet.getRow(1);
      detailHeader.font = { bold: true };
      detailHeader.alignment = { horizontal: "center" };

      detailHeader.eachCell(cell => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "00B0F0" }
        };
        cell.font = { color: { argb: "FFFFFF" }, bold: true };
      });

      // 🔥 LLENAR DETALLE (SIN MÁS REQUESTS)
      data.forEach(p => {
        const detallesFiltrados = allDetalles.filter(d =>
          d.Numero_PO === p.Numero_PO &&
          d.oid_municipio === p.oid_municipio &&
          d.oid_proyecto === p.oid_proyecto &&
          (d.observacion ?? null) === (p.observacion ?? null)
        );

        detallesFiltrados.forEach(d => {
          detailSheet.addRow({
            Numero_PO: p.Numero_PO,
            nombre_proyecto: p.nombre_proyecto,
            municipio: p.municipio,
            nombre_producto: d.nombre_producto,
            cantidad_total: formatNumber(d.cantidad_total),
            cantidad_entregada: formatNumber(d.cantidad_entregada),
            pendiente: formatNumber(d.pendiente),
            valor_unitario_COP: formatCurrency(d.valor_unitario_COP),
            valor_total: formatCurrency(d.valor_total),
            porcentaje_entrega: `${Number(d.porcentaje_entrega || 0)}%`
          });
        });
      });

      // ===============================
      // 🔹 AUTO AJUSTE COLUMNAS
      // ===============================
      [sheet, detailSheet].forEach(ws => {
        ws.columns.forEach(col => {
          let maxLength = col.header.length;

          col.eachCell?.({ includeEmpty: true }, cell => {
            const length = cell.value ? cell.value.toString().length : 10;
            if (length > maxLength) maxLength = length;
          });

          col.width = maxLength + 2;
        });
      });

      // 🔥 DESCARGA
      const buffer = await workbook.xlsx.writeBuffer();

      saveAs(
        new Blob([buffer]),
        `reporte_productos_OC_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      message.success("Excel generado correctamente con detalle");

    } catch (error) {
      console.error(error);
      message.error("Error generando Excel");
    }
  };

  return (
    <Button
      icon={<DownloadOutlined />}
      type="primary"
      onClick={handleExport}
    >
      Exportar Excel
    </Button>
  );
}